/**
 * widget 定位的唯一真實來源。
 *
 * 舊架構把「位置」拆在三個地方：宿主頁 launch.js 的指令、livechat-iframe.css 的
 * class、App.tsx 的 makeStyles。這個模組把三者收成一組純函式 —— 全部以 viewport
 * 座標運算，iframe 外框只是照抄結果的一個矩形。
 *
 * 所有函式都不碰 DOM、不讀 window，方便 geometry.selfcheck.ts 直接驗證。
 */

export type Edge = 'left' | 'right';

/**
 * 使用者位置的持久化形狀：水平與垂直都自由，各存「可用行程的比例」。
 *
 * 存比例而不是絕對座標 —— 視窗縮放或手機轉向後位置仍然合理，不會跑出畫面。
 */
export interface Anchor {
	x: number;
	y: number;
}

export interface Rect {
	top: number;
	left: number;
	width: number;
	height: number;
}

export interface Size {
	width: number;
	height: number;
}

export interface Viewport {
	width: number;
	height: number;
}

/** 邊距。預設沿用舊 CSS，後台 positionSettings 可覆寫 */
export interface Margins {
	x: number;
	y: number;
}

/** 沿用舊 #eight-chat-content 收起時的 80x80（60px 圖示 + 四周 10px 留白給陰影） */
export const LAUNCHER_BOX = 80;

/** 沿用舊 .rightContent/.leftContent 的 right/left:10px 與 #eight-chat-content 的 bottom:10px */
export const DEFAULT_MARGIN = 10;

/** launcher 外框與浮層之間的間距 */
export const SURFACE_GAP = 10;

/** 超過這個位移才算拖動；未達則視為點擊。同時掩護外框撐開的一帧延遲 */
export const DRAG_THRESHOLD = 8;

/**
 * 浮層最低可用高度的預設值 —— 只在量到真值之前用。
 *
 * 面板真正不可壓縮的是「標題列 + rich menu + 輸入列 + 留白」，而 rich menu 是後台可
 * 設的（有沒有、幾列都會變），寫死的數字一定在某些 org 上失準。所以 App 掛載後會實
 * 際量一次再傳進 placeSurface，這個常數只負責量到之前的第一帧。
 */
export const SURFACE_MIN_HEIGHT = 160;

const clamp = (value: number, min: number, max: number): number => (max < min ? min : Math.min(Math.max(value, min), max));

/** launcher 可移動的行程。viewport 太小時為 0 */
const travelX = (viewport: Viewport, margins: Margins): number => Math.max(viewport.width - LAUNCHER_BOX - margins.x * 2, 0);

const travelY = (viewport: Viewport, margins: Margins): number => Math.max(viewport.height - LAUNCHER_BOX - margins.y * 2, 0);

/**
 * 後台 positionSettings 換算成邊距。
 *
 * 舊 launch.js 只套用 truthy 值（`if (positionSettings[key])`），0 會退回 CSS 預設的
 * 10px，這裡照舊行為，否則首次造訪的位置會跟變更前差幾個 px。
 */
export const marginsFromSettings = (styleData: any): Margins => {
	const settings = styleData?.positionSettings || {};
	return {
		x: Number(settings.right) || Number(settings.left) || DEFAULT_MARGIN,
		y: Number(settings.bottom) || Number(settings.top) || DEFAULT_MARGIN,
	};
};

/** 後台的 position 欄位。`freedom` 才允許使用者拖動 */
export const POSITION_FREEDOM = 'freedom';
export const POSITION_BOTTOM_RIGHT = 'bottom-right';
export const POSITION_BOTTOM_LEFT = 'bottom-left';

/**
 * 這個 org 是否允許使用者自行擺放 launcher。
 *
 * 只有 `freedom` 可以拖動；`bottom-right` / `bottom-left`（以及未設定）維持後台指定的
 * 固定位置，即使瀏覽器裡存過拖動位置也不採用 —— 否則「維持原來的擺放位置」不成立。
 */
export const isDraggablePosition = (styleData: any): boolean => styleData?.position === POSITION_FREEDOM;

/**
 * 後台 position 換算成錨點。
 *
 * `bottom-right` 與 `freedom` 都落在右下角（freedom 的預設位置就是右下）；其餘（含
 * 未設定）一律靠左 —— 比照舊 `App.tsx` 只認 `'bottom-right'` 的判斷，未設定的 org
 * 才不會換邊。y 固定為 1（貼齊下緣），等同舊行為的 bottom 定位。
 */
export const anchorFromSettings = (styleData: any): Anchor => ({
	x: styleData?.position === POSITION_BOTTOM_RIGHT || styleData?.position === POSITION_FREEDOM ? 1 : 0,
	y: 1,
});

/** 錨點 → launcher 在 viewport 中的矩形 */
export const launcherRect = (anchor: Anchor, viewport: Viewport, margins: Margins): Rect => {
	const maxLeft = Math.max(viewport.width - LAUNCHER_BOX - margins.x, 0);
	const maxTop = Math.max(viewport.height - LAUNCHER_BOX - margins.y, 0);

	return {
		top: clamp(margins.y + anchor.y * travelY(viewport, margins), Math.min(margins.y, maxTop), maxTop),
		left: clamp(margins.x + anchor.x * travelX(viewport, margins), Math.min(margins.x, maxLeft), maxLeft),
		width: LAUNCHER_BOX,
		height: LAUNCHER_BOX,
	};
};

/** launcher 中心落在畫面哪一半 —— 決定浮層往哪一側展開 */
export const launcherSide = (launcher: Rect, viewport: Viewport): Edge =>
	launcher.left + launcher.width / 2 < viewport.width / 2 ? 'left' : 'right';

/**
 * 拖動放開時的落點 → 錨點。
 *
 * 不做邊緣吸附 —— 使用者放哪就是哪。只把座標換算成比例並夾在 0..1，所以拖出畫面
 * 邊界時會被拉回可見範圍。
 */
export const positionFromBox = (box: { top: number; left: number }, viewport: Viewport, margins: Margins): Anchor => {
	const tx = travelX(viewport, margins);
	const ty = travelY(viewport, margins);

	return {
		x: tx > 0 ? clamp((box.left - margins.x) / tx, 0, 1) : 0,
		y: ty > 0 ? clamp((box.top - margins.y) / ty, 0, 1) : 0,
	};
};

/**
 * 浮層（面板／通知／Preview）依 launcher 錨點避讓。
 *
 * 水平貼在 launcher 的反側：右錨時浮層右緣對齊 launcher 右緣、本體往左長，左錨反之。
 * 垂直朝可用空間較大的一側展開，最後整體夾進 viewport。
 */
export const placeSurface = (launcher: Rect, surface: Size, viewport: Viewport, margins: Margins, minHeight?: number): Rect => {
	const width = Math.min(surface.width, Math.max(viewport.width - margins.x * 2, 0));

	// 水平對齊 launcher 較靠近的那一側，本體往畫面內側長
	const edge = launcherSide(launcher, viewport);
	const rawLeft = edge === 'right' ? launcher.left + launcher.width - width : launcher.left;
	const maxLeft = Math.max(viewport.width - width - margins.x, margins.x);

	const viewportSpace = Math.max(viewport.height - margins.y * 2, 0);
	const wanted = Math.min(surface.height, viewportSpace);

	// 優先貼在 launcher 空間較大的那一側，且只用那一側的空間 —— 不覆蓋 launcher
	const spaceAbove = Math.max(launcher.top - margins.y - SURFACE_GAP, 0);
	const spaceBelow = Math.max(viewport.height - (launcher.top + launcher.height) - margins.y - SURFACE_GAP, 0);
	const openUp = spaceAbove >= spaceBelow;
	const sideSpace = openUp ? spaceAbove : spaceBelow;

	/**
	 * 側邊空間夠不夠放一個「還能用」的浮層。
	 *
	 * minHeight 是呼叫端說得出口的最低高度 —— 面板傳「不可壓縮的那幾列」的量測值，
	 * 因為面板的訊息區可以壓縮，擠一點仍然能讀能打字；不傳的浮層（通知泡泡）則以自
	 * 身高度為底，它沒有可壓縮的部分，裁掉就是裁掉內容。
	 *
	 * 夠 → 貼在該側，高度取該側空間，不覆蓋 launcher（擠一點也照貼）。
	 * 不夠 → 連最低高度都放不下，改用整個可視高度並垂直貼近 launcher。這時會蓋住
	 *        launcher，但 launcher 疊在面板之上仍可點、面板也有關閉鈕；相較於一個
	 *        連輸入列都塞不進去的面板，可用性優先於不重疊。
	 */
	const floor = Math.min(minHeight === undefined ? wanted : minHeight, wanted);
	const fitsOnSide = sideSpace >= floor;

	const height = fitsOnSide ? Math.min(wanted, sideSpace) : wanted;
	const maxTop = Math.max(viewport.height - height - margins.y, margins.y);
	const rawTop = fitsOnSide
		? openUp
			? launcher.top - SURFACE_GAP - height
			: launcher.top + launcher.height + SURFACE_GAP
		: launcher.top + launcher.height / 2 - height / 2;

	return {
		top: clamp(rawTop, margins.y, maxTop),
		left: clamp(rawLeft, margins.x, maxLeft),
		width,
		height,
	};
};

export const viewportRect = (viewport: Viewport): Rect => ({
	top: 0,
	left: 0,
	width: viewport.width,
	height: viewport.height,
});

/**
 * 回報給宿主頁的「可互動矩形」清單。
 *
 * 刻意回傳多個矩形而不是聯集 —— launcher 與浮層之間的空隙、以及 launcher 旁邊那塊
 * （浮層比 launcher 寬，聯集會把下緣整條都吃進來）都必須讓宿主頁收到點擊。舊架構只有
 * 一個外框，非聯集不可，那塊區域就一直擋著宿主頁。
 *
 * 宿主頁把這些矩形組成 clip-path 的多重子路徑，裁切區外同時不接受指標事件、也不繪製。
 */
export const hitZones = (viewport: Viewport, launcher: Rect | null, surface: Rect | null, fullscreen: boolean): Rect[] => {
	if (fullscreen) {
		return [viewportRect(viewport)];
	}

	const zones: Rect[] = [];
	if (launcher) {
		zones.push(launcher);
	}
	if (surface) {
		zones.push(surface);
	}
	return zones;
};

// ── 位置的序列化 ────────────────────────────────────────────────
//
// 格式刻意用 `edge:offsetY` 而不是 JSON —— 同一份字串在 localStorage 與宿主頁
// cookie 兩條路上都能直接存，不需要處理 cookie 的字元轉義。
// 放在這裡（而不是 positionStorage.ts）是為了保持零依賴，自檢才能直接載入。

const POSITION_KEY_PREFIX = 'super8_livechat_position_';

/**
 * 位置依 org 區隔，不同 org 的 widget 互不覆寫。
 *
 * orgId 要編碼 —— 加密後的 org id 可能含 `=`（例如 `@@xxxx==`），直接當 cookie 名稱時
 * 瀏覽器會在第一個 `=` 斷開，實際存下的 name 尾巴被截掉。localStorage 不在意，但退路
 * 走的是 cookie，所以統一編碼，兩條路用同一個 key。
 */
export const positionStorageKey = (orgId: string): string => `${POSITION_KEY_PREFIX}${encodeURIComponent(orgId)}`;

export const serializeAnchor = (anchor: Anchor): string => `${anchor.x.toFixed(4)},${anchor.y.toFixed(4)}`;

/** 解析失敗（壞資料、越界、舊的 `edge:offsetY` 格式）一律回 null，呼叫端會退回後台設定 */
export const parseAnchor = (raw: string | null | undefined): Anchor | null => {
	if (!raw) {
		return null;
	}

	const parts = raw.split(',');
	if (parts.length !== 2) {
		return null;
	}

	// 不能只靠 Number()：`Number('')` 是 0，會讓 '0.5,' 這種殘缺字串被當成合法位置
	if (!parts[0].trim() || !parts[1].trim()) {
		return null;
	}

	const x = Number(parts[0]);
	const y = Number(parts[1]);
	if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || x > 1 || y < 0 || y > 1) {
		return null;
	}

	return { x, y };
};
